import { AdjustmentType } from "utility/enums/budget-allocation";
import { IBaseStepHistory } from "./../StepHistoryDetail";
import { TransferFrom } from "utility/enums/budget-allocation";

export interface IBudgetAllocationStatus {
	budgetAllocationStatusId: number;
	name: string;
}

export interface IBudgetAllocationNumber {
	budgetAllocationId: number;
	budgetAllocationNumber: string;
}

export interface IBudgetGlCategory {
	glCategoryId: number;
	name: string;
}

export interface IBudgetAllocationType {
	budgetAllocationTypeId: number;
	name: string;
}

export interface IBudgetTransactionType {
	budgetTransactionTypeId: number;
	name: string;
}

export class BudgetAllocationHeaderDetail {
	budgetAllocationId: number | null = null;
	budgetAllocationNumber: string = "";
	isAllocatedByProgramme: boolean = false;
	isExcelUpload: boolean = false;
	erpDbCompanyId: number | null = null;
	financialYearId: number = -1;
	budgetingStageId: number | null = null;
	budgetingStage: string = "";
	budgetAllocationStatusId: number | null = null;
	controlTotal: number | null = null;
	budgetAllocationTypeId: number | null = null;
	budgetTransactionTypeId: number | null = null;
	description: string = "";
	createdDate: Date = new Date();
	createdBy: string = "";
	stepDetail: BudgetAllocationStepDetail | null = null;
	isCreatedByMe: boolean = false;
	statusText: string = ""; // Required for FE to bind status in textfield
}

export interface IProgrammeDepartment {
	departmentId: number;
	name: string;
	description: string;
	manager: string;
	budgetTypeId: number;
	preAllocationAmount: number;
}

export interface IProgrammeDeptByErpDbCompany {
	programmeId: number;
	name: string;
	programmeNumber: string;
	programmeDepartments: IProgrammeDepartment[];
}

export interface IAddBudgetAllocationBaseDetail {
	budgetAllocationLineId: number | null;
	budgetAllocationId: number | null;
	programmeId: number;
	departmentId: number;
	budgetTypeId: number;
	baselineAllocationAmount: number;
}

export interface IUpdateBudgetAllocationDetail {
	description: string;
	controlTotal: number;
	budgetAllocationLineInputs: IAddBudgetAllocationBaseDetail[];
}

export interface IAddBudgetAllocationDetail
	extends IUpdateBudgetAllocationDetail {
	budgetAllocationNumber: string;
	isAllocatedByProgramme: boolean;
	erpDbCompanyId: number;
	financialYearId: number;
	budgetAllocationTypeId: number;
	budgetTransactionTypeId: number;
	budgetingStageId: number;
}

class ProgrammeBaseDetail {
	programmeId: number = 0;
	programmeName: string = "";
	programmeNumber: string = "";
	isChecked: boolean = false;
	isDeptGroupChecked: boolean = false;
}

class DepartmentBaseDetail {
	departmentId: number = 0;
	departmentName: string = "";
	isChecked: boolean = false;
	managerName: string = "";
	budgetTypeId: number = 0;
	isAdjustmentAllow: boolean = false;
	isTransferAllow: boolean = false;
	isAnyAdjustmentOrTransferDone: boolean = false;
	isApproved: boolean = false;
	isRejected: boolean = false;
	isChangeRequested: boolean = false;
	isChangeRequestCompleted: boolean = false;
	hasAdjustment: boolean = false;
	hasTransfer: boolean = false;
	isChangeRequestButtonShow: boolean = false;
	isChangeOrderCompleted: boolean | null = null;
	baselineAllocationAmount: number = 0;
	adjustmentAllocationAmount: number = 0;
	transferAllocationAmount: number = 0;
	preAllocationAmount: number = 0;
	isLineStatusUpdated: boolean = false; // Required for FE to maintain if user has changed status for line detail or not
}

// Required to bind detail if status = "New" || "Draft"
export class DeptDetailByBudgetType extends DepartmentBaseDetail {
	financeAllocation: number = 0;
	departmentAllocation: number = 0;
	variance: number = 0;
	budgetAllocationLineId: number = 0;
	budgetAllocationLineStatusId: number = 0;
	budgetAllocationLineAdjustmentId: number | null = null;
	budgetAllocationLineTransferId: number | null = null;
	changeRequestBudgetAllocationStepId: number | null = null;
	lineComment: string | null = null;
	isApproveButtonShow: boolean = false;
	isRejectButtonShow: boolean = false;
	isAdjustmentTransferChangeRequested: boolean = false;
	isAllocationLineRejected: boolean = false;
}

export class ProgrammeDetailByDbCompany extends ProgrammeBaseDetail {
	budgetTypeId: number = 0;
	departmentList: DeptDetailByBudgetType[] = [];
}

export interface IFinancialYearBudgetDetail {
	financialYear: string;
	startDate: Date;
	endDate: Date;
}

// Models for GetBudgetAllocationDetail  API

export interface IGetDepartment {
	budgetAllocationLineId: number;
	budgetAllocationLineStatusId: number;
	departmentId: number;
	budgetTypeId: number;
	name: string;
	description: string;
	manager: string;
	isAdjustmentAllow: boolean;
	isTransferAllow: boolean;
	isAnyAdjustmentOrTransferDone: boolean;
	isApproved: boolean;
	isRejected: boolean;
	isChangeRequested: boolean;
	isChangeRequestCompleted: boolean;
	isChangeRequestButtonShow: boolean;
	isApproveButtonShow: boolean;
	isRejectButtonShow: boolean;
	isChangeOrderCompleted: boolean | null;
	isAdjustmentTransferChangeRequested: boolean;
	isAllocationLineRejected: boolean;
	hasAdjustment: boolean;
	hasTransfer: boolean;
	preAllocationAmount: number | null;
	finalAllocationAmount: number;
	baselineAllocationAmount: number;
	departmentAllocationAmount: number | null;
	adjustmentAllocationAmount: number | null;
	transferAllocationAmount: number | null;
	budgetAllocationLineAdjustmentId: number | null;
	budgetAllocationLineTransferId: number | null;
	changeRequestBudgetAllocationStepId: number | null;
	lineComment: string | null;
}

export interface IGetProgrammeDepartments {
	programmeId: number;
	programmeNumber: string;
	name: string;
	departments: IGetDepartment[];
}

export class BudgetAllocationStepDetail {
	budgetAllocationStepId: number = 0;
	budgetAllocationStepDepartmentId: number = 0;
	budgetAllocationStepStatusId: number = 0;
	stepNumber: number | null = null;
	sequenceNumber: number | null = null;
	modulePhaseId: number = 0;
	isSaveSubmitButtonShow: boolean = false;
	isReSubmitButtonShow: boolean = false;
	isTaskCompletedByMe: boolean = false;
	workflowStepTypeId: number = 0;
	isModulePhaseInLastStep: boolean = false; // Set true when module phase is "Submitted for Department Allocation" and is last step
	isMainStep: boolean = false; // Set true when step is main step (i.e. "1.0 , 2.0, 3.0" etc.)
}

export class FinanceAllocationDetail {
	budgetAllocationId: number = -1;
	budgetAllocationNumber: string = "";
	controlTotal: number | null = null;
	finAllocationList: IGetProgrammeDepartments[] = [];
}

export class CommentDetailByDeptName {
	departmentName: string = "";
	comment: string = "";
}

// Comment detail model to bind comment in department allocation fly in window when status for budget allocation line is change requested for department allocation
export class BudgetAllocationLineCommentDetail {
	programmeId: number = 0;
	budgetTypeId: number = 0;
	commentList: CommentDetailByDeptName[] = [];
}

export class DepartmentAllocationDetail {
	budgetAllocationId: number = -1;
	budgetAllocationNumber: string = "";
	deptAllocationList: IGetDeptAllocationProgDept[] = [];
	budgetTypeTotal: BudgetAllocationTotal = new BudgetAllocationTotal();
	budgetAllocationLineComments: BudgetAllocationLineCommentDetail[] = [];
}

// Required for FE to check if allocation detail is updated or not
export class BudgetAllocationDetail {
	allocationHeaderDetail: BudgetAllocationHeaderDetail =
		new BudgetAllocationHeaderDetail();
	financeAllocationDetail?: FinanceAllocationDetail;
	departmentAllocationDetail?: DepartmentAllocationDetail;
}

// Models for Budget Allocation Filter, Tile, Allocation Tab content
export class AllocationGridDetailModel {
	budgetAllocationLineId: number | null = null;
	programmeName: string = "";
	programmeNumber: string = "";
	programmeId: number = 0;
	departmentName: string = "";
	departmentId: number = 0;
	revenueAllocationAmount: number = 0;
	revenueCaptured: number = 0;
	revenueVariance: number = 0;
	opexAllocationAmount: number = 0;
	opexCaptured: number = 0;
	opexVariance: number = 0;
	capexAllocationAmount: number = 0;
	capexCaptured: number = 0;
	capexVariance: number = 0;
}

export class BudgetAllocationTileDetail {
	totalRevenueBudget: number = 0;
	totalCapexBudget: number = 0;
	totalOpexBudget: number = 0;
}

export class BudgetAllocationFilterModel {
	departmentIds: string[];
	financialYearIds: number | null;
	glAccountIds: string[];
	glCategoryIds: string[];
	budgetTypeIds: string[];

	constructor() {
		this.departmentIds = [];
		this.financialYearIds = null;
		this.glAccountIds = [];
		this.glCategoryIds = [];
		this.budgetTypeIds = [];
	}
}

export interface IBudgetAllocationStepHistory extends IBaseStepHistory {
	budgetAllocationStepId: number;
	budgetAllocationStepDepartmentId: number;
	stepStatusId: number;
	budgetAllocationId: number;
	stepStatus: string;
	modulePhaseId: number;
	workflowStepTypeId: number;
	sequenceNumber: number;
}

export class BudgetAllocationStepHistoryEmitter {
	budgetAllocationId: number = -1;
	budgetAllocationStepId: number = -1;
}

// Required to bind line detail in allocation tab
export interface IAllocationTabLineDetail {
	budgetAllocationLineId: number;
	programmeNumber: string;
	programmeName: string;
	departmentId: number;
	departmentName: string;
	opexAllocationAmount: number;
	capexAllocationAmount: number;
	revenueAllocationAmount: number;
}

// Department Budget Allocation

// Required to bind detail if status = "Submitted for departmental allocation"
export class DeptDetailForDeptAllocation extends DepartmentBaseDetail {
	finalAllocationAmount: number = 0;
	glCategory: string = "";
	glCategoryId: number = 0;
	glAccount: string = "";
	glAccountDescription: string = "";
	glAccountId: number = 0;
	budgetTypeName: string = "";
	budgetAllocationLineId: number = 0;
	budgetAllocationLineDetailId: number | null = null;
	changeOrder: number = 0;
	item: string = "";
	itemId: number = 0;
	budgetAllocationLineDetailStatusId: number | null = null;
	budgetAllocationLineStatusId: number = 0;
	budgetAllocationLineDetailAdjustmentId: number | null = null;
	budgetAllocationLineDetailTransferId: number | null = null;
	changeRequestBudgetAllocationStepDepartmentId: number | null = null;
	lineComment: string = "";
	isMonthlyEvenSpreadDone: boolean = false;
	hasAllocatedAmountUpdated: boolean = false; // Required for FE to check if allocation amount is change or not, if monthly allocation detail is already saved
}

export class DeptAllocationProgDetail extends ProgrammeBaseDetail {
	budgetTypeId: number = 0;
	departmentList: DeptDetailForDeptAllocation[] = [];
}

export class BudgetAllocationTotal {
	opexAllocationAmount: number = 0;
	capexAllocationAmount: number = 0;
	revenueAllocationAmount: number = 0;
}

export interface IBudgetAllocationLineDetail {
	budgetAllocationLineDetailId: number | null;
	finalAllocationAmount: number;
	preAllocationAmount: number;
	budgetAllocationLineId: number;
	departmentId: number;
	name: string;
	description: string;
	manager: string;
	glAccountId: number;
	glAccount: string;
	glAccountDescription: string;
	glCategory: string | null;
	glCategoryId: number | null;
	budgetTypeId: number;
	budgetType: string;
	budgetAllocationLineDetailStatusId: number | null;
	budgetAllocationLineStatusId: number;
	itemId: number;
	item: string;
	baselineAllocationAmount: number;
	adjustmentAllocationAmount: number;
	transferAllocationAmount: number;
	isAdjustmentAllow: boolean;
	isTransferAllow: boolean;
	isAnyAdjustmentOrTransferDone: boolean;
	isMonthlyEvenSpreadDone: boolean;
	isApproved: boolean;
	isRejected: boolean;
	isChangeRequested: boolean;
	isChangeRequestCompleted: boolean;
	hasAdjustment: boolean;
	hasTransfer: boolean;
	isChangeRequestButtonShow: boolean;
	isChangeOrderCompleted: boolean | null;
	lineComment: string;
	budgetAllocationLineDetailAdjustmentId: number | null;
	budgetAllocationLineDetailTransferId: number | null;
	changeRequestBudgetAllocationStepDepartmentId: number | null;
}

export interface IGetDeptAllocationProgDept {
	programmeId: number;
	programmeNumber: string;
	name: string;
	budgetAllocationLineDetails: IBudgetAllocationLineDetail[];
}

// To bind master budget detail in grid
export class MasterBudgetGridDetail {
	budgetAllocationLineDetailId: number | null = null;
	budgetAllocationLineId: number | null = null;
	programmeName: string = "";
	programmeNumber: string = "";
	programmeId: number = 0;
	departmentName: string = "";
	departmentId: number = 0;
	glAccountId: number = 0;
	glAccount: string = "";
	glAccountDescription: string = "";
	glCategoryId: number = 0;
	glCategory: string = "";
	budgetTypeId: number = 0;
	budgetTypeName: string = "";
	initialAllocation: number = 0;
	changeOrder: number = 0;
	finalAllocation: number = 0;
	itemId: number = 0;
	item: string = "";
	budgetAllocationLineDetailStatusId: number | null = null;
	isMonthlyEvenSpreadDone: boolean = false;
	hasAllocatedAmountUpdated: boolean = false; // Required for FE to check if allocation amount is change or not, if monthly allocation detail is already saved
}

export interface IAddDeptAllocationLineDetail {
	budgetAllocationLineDetailId: number | null;
	budgetAllocationLineId: number;
	glAccountId: number;
	baselineAllocationAmount: number;
	itemId: number;
}

export type BudgetDetailByFinancialYear = {
	financialYear: string;
	forecastAmount: number;
	erpDbCompany: string;
	departmentCount: number;
};

export type ICopyProgDeptDetailByBudgetType = {
	budgetTypeId: number;
	departmentId: number;
	finalAllocationAmount: number;
	programmeId: number;
};

export type IGetCopyProgDeptDetail = {
	programmeId: number;
	departments: ICopyProgDeptDetailByBudgetType[];
};

export class BudgetAllocationCopyDetailModel {
	budgetAllocationId: number = 0;
	budgetAllocationNumber: string = "";
	finAllocationList: IGetCopyProgDeptDetail[] = [];
	deptAllocationList: IGetCopyProgDeptDetail[] = [];
}

export interface IBaseAdjustmentHistory {
	lineNo: number;
	allocationAmount: number;
	additionalAllocationAmount: number;
	totalAdjustmentAllocationAmount: number;
	allocationTypeId: number;
}

// Finance Allocation adjustment detail

export class BudgetAllocationFinanceAdjustmentDetail {
	budgetAllocationLineId: number = -1;
	budgetAllocationLineAdjustmentId: number = -1;
	budgetTypeId: number = 0;
	adjustmentNumber: string = "";
	department: string = "";
	adjustmentStatus: string = "";
	currentAllocation: number = 0;
	preAllocationAmount: number = 0;
	additionalAllocation: number | null = null;
	allocationTypeId: number | null = null;
	createdBy: string = "";
	comment: string = "";
	createdDate: Date = new Date();
	adjustmentStatusId: number | null = null;
}

export class BudgetFinanceAllocationAdjustment {
	budgetAllocationLineId: number = 0;
	budgetTypeId: number = 0;
	allocationTypeId: number = AdjustmentType.Increase;
	allocationAmount: number = 0;
	additionalAllocationAmount: number | null = null;
	comment: string = "";
	budgetAllocationStepId: number = 0;
}

// For budget allocation transfer and adjustment attachment detail
export interface IBaseAdjTransferAttachment {
	documentFileName: string;
	documentDescription: string;
	documentFilePath: string;
	stepNumber?: number;
	sequenceNumber?: number;
}

export interface IFinAllocationAdjAttachment
	extends IBaseAdjTransferAttachment {
	budgetAllocationLineAdjustmentAttachmentId: number;
}

export interface IFinanceAdjustmentHistory extends IBaseAdjustmentHistory {
	budgetAllocationLineAdjustmentHistoryId: number;
}

export interface IBudgetAllocationFinanceAdjGridDetail {
	budgetAllocationLineAdjustmentId: number;
	budgetAllocationLineId: number;
	adjustmentNumber: string;
	budgetType: string;
	budgetAllocationLineAdjustmentStatusId: number;
	status: string;
	adjustmentHistory: IFinanceAdjustmentHistory[];
}

// Department Allocation adjustment detail

export class BudgetAllocationDeptAdjustmentDetail {
	budgetAllocationLineDetailAdjustmentId: number = -1;
	budgetAllocationLineDetailId: number = -1;
	adjustmentNumber: string = "";
	department: string = "";
	item: string = "";
	glCategory: string = "";
	glAccount: string = "";
	glAccountDescription: string = "";
	adjustmentStatus: string = "";
	currentAllocation: number = 0;
	preAllocationAmount: number = 0;
	additionalAllocation: number | null = null;
	allocationTypeId: number | null = null;
	createdBy: string = "";
	comment: string = "";
	createdDate: Date = new Date();
	adjustmentStatusId: number | null = null;
}

export class BudgetDeptAllocationAdjustment {
	budgetAllocationLineDetailId: number = 0;
	budgetAllocationStepDepartmentId: number = 0;
	allocationTypeId: number = AdjustmentType.Increase;
	allocationAmount: number = 0;
	additionalAllocationAmount: number | null = null;
	comment: string = "";
}

export interface IDeptAllocationAdjAttachment
	extends IBaseAdjTransferAttachment {
	budgetAllocationLineDetailAdjustmentAttachmentId: number;
}

export interface IDepartmentAdjustmentHistory extends IBaseAdjustmentHistory {
	budgetAllocationLineDetailAdjustmentHistoryId: number;
}

export interface IBudgetAllocationDeptAdjGridDetail {
	budgetAllocationLineDetailAdjustmentId: number;
	budgetAllocationLineDetailId: number;
	budgetAllocationLineDetailAdjustmentStatusId: number;
	adjustmentNumber: string;
	status: string;
	adjustmentHistory: IDepartmentAdjustmentHistory[];
}

// Budget Transfer

export class TransferSectionAllocationDetail {
	leftSectionCurrentAllocation: number = 0;
	leftSectionPostAllocation: number = 0;
	unallocated: number = 0;
	rightSectionCurrentAllocation: number = 0;
	rightSectionPostAllocation: number = 0;
}

// For Transfer Grid Detail - Required for FE only
export class BudgetAllocationTransferDetailModel {
	transferFromId: number = TransferFrom.Source;
	programmeId: number | null = null;
	departmentId: number | null = null;
	itemId?: number | null = null;
	budgetTypeId?: number | null = null;
	transferIn: number | null = null;
	transferOut: number | null = null;
	comment: string = "";
	glAccount?: string = "";
	glAccountDescription?: string = "";
	budgetType: string = "";
	transferType: string = "";
	rowNo: number = -1;
	programme: string = "";
	department: string = "";
	item?: string = "";
}

export class BaseTransferHeaderDetail {
	transferNumber: string = "";
	transferFrom: number | null = TransferFrom.Source;
	comment: string | null = null;
	createdDate: string | null = null;
	createdBy: string | null = null;
	status: string = "";
	statusId: number | null = null;
}

// Required to bind department dropdown list in right section detail - Dept / Finance Transfer
export interface ITransferDepartment {
	departmentId: number;
	name: string;
	description: string;
	programmeId: number;
}

// Finance Allocation transfer detail
export interface IFinAllocationTransferAttachment
	extends IBaseAdjTransferAttachment {
	budgetAllocationLineTransferAttachmentId: number;
}

export interface IFinAllocationTransferGridDetail {
	budgetAllocationLineTransferHistoryId: number;
	budgetAllocationLineTransferId: number;
	historyBudgetAllocationLineId: number;
	headerBudgetAllocationLineId: number;
	budgetAllocationLineId: number;
	programmeId: number;
	departmentId: number;
	budgetTypeId: number;
	transferNumber: string;
	budgetType: string;
	department: string;
	currentAllocation: number;
	transferOut: number | null;
	transferIn: number | null;
	postAllocation: number;
	statusId: number;
	status: string;
	programmeNumber: string;
}

export interface IFinanceTransferHistory {
	budgetAllocationLineTransferHistoryId: number | null;
	budgetAllocationLineId: number;
	programmeId: number;
	departmentId: number | null;
	budgetTypeId: number | null;
	allocationAmount: number;
	transferAmount: number;
}

// save request model
export class FinanceTransferDetailModel {
	budgetAllocationLineTransferId: number | null = null;
	budgetAllocationId: number = -1;
	budgetAllocationLineId: number = -1;
	budgetTypeId: number = -1;
	transferFrom: number = TransferFrom.Source;
	comment: string = "";
	budgetAllocationLineTransferHistory: IFinanceTransferHistory[] = [];
	budgetAllocationStepId: number = 0;
}

// Required to bind Finance Transfer Left / Right Section Detail
export class FinTransferSectionDetail {
	budgetAllocationLineId: number = -1;
	programmeNumber: string = "";
	department: string = "";
	budgetType: string = "";
	budgetTypeId: number = -1;
	currentAllocation: number = 0;
	preAllocation: number = 0;
	transferAmount: number | null = null;
}

// Get Finance Transfer Detail API model - includes header detail, left section and right section detail
export class IGetFinanceTransferDetail extends BaseTransferHeaderDetail {
	budgetAllocationLineTransferId: number | null = null;
	leftSideTransferDetail: FinTransferSectionDetail =
		new FinTransferSectionDetail();
	rightSideTransferDetail: FinTransferSectionDetail | null = null;
}

// Required to bind Finance Transfer Right Section Dropdown List
export interface ITransferProgDeptAllocationDetail {
	rowNo: number;
	budgetAllocationLineId: number | null;
	programmeId: number;
	programmeNumber: string;
	departmentId: number;
	name: string;
	description: string;
	budgetTypeId: number;
	budgetType: string;
	preAllocationAmount: number;
	finalAllocationAmount: number;
}

// Required to bind budget type dropdown list in right section detail - Finance Transfer
export interface ITransferBudgetType {
	budgetTypeId: number;
	name: string;
	departmentId: number;
	programmeId: number;
}

// Department Allocation transfer detail
export interface IDeptAllocationTransferAttachment
	extends IBaseAdjTransferAttachment {
	budgetAllocationLineDetailTransferAttachmentId: number;
}

export interface IDeptAllocationTransferGridDetail {
	budgetAllocationLineDetailTransferHistoryId: number;
	budgetAllocationLineDetailTransferId: number;
	historyBudgetAllocationLineDetailId: number;
	headerBudgetAllocationLineDetailId: number;
	budgetAllocationLineId: number;
	transferNumber: string;
	department: string;
	item: string;
	glAccount: string;
	glAccountDescription: string;
	currentAllocation: number;
	transferOut: number | null;
	transferIn: number | null;
	postAllocation: number;
	statusId: number;
	status: string;
	glAccountId: number | null;
	itemId: number | null;
	programmeNumber: string;
	departmentId: number | null;
	programmeId: number | null;
}

export interface IDepartmentTransferHistory {
	budgetAllocationLineDetailTransferHistoryId: number | null;
	budgetAllocationLineDetailId: number;
	budgetAllocationLineId: number;
	itemId: number | null;
	glAccountId: number | null;
	allocationAmount: number;
	transferAmount: number;
}

// save request model
export class DepartmentTransferDetailModel {
	budgetAllocationLineDetailTransferId: number | null = null;
	budgetAllocationId: number = -1;
	budgetAllocationLineDetailId: number = -1;
	budgetAllocationStepDepartmentId: number = -1;
	transferFrom: number = TransferFrom.Source;
	comment: string = "";
	budgetAllocationLineDetailTransferHistory: IDepartmentTransferHistory[] = [];
}

// Required to bind Department Transfer Left Section Detail
export class DeptTransferSectionDetail {
	budgetAllocationLineDetailId: number = -1;
	budgetAllocationLineId: number = -1;
	programmeNumber: string = "";
	department: string = "";
	item: string = "";
	glAccount: string = "";
	glAccountDescription: string = "";
	budgetType: string = "";
	currentAllocation: number = 0;
	preAllocationAmount: number = 0;
	transferAmount: number | null = null;
}

// Get Department Transfer Detail API model - includes header detail, left section and right section detail
export class IGetDepartmentTransferDetail extends BaseTransferHeaderDetail {
	budgetAllocationLineDetailTransferId: number | null = null;
	leftSideTransferDetail: DeptTransferSectionDetail =
		new DeptTransferSectionDetail();
	rightSideTransferDetail: DeptTransferSectionDetail | null = null;
}

// Required to bind Department Transfer Right Section Dropdown List
export interface ITransferProgGlAllocationDetail {
	rowNo: number;
	budgetAllocationLineDetailId: number | null;
	budgetAllocationLineId: number;
	programmeId: number;
	programmeNumber: string;
	departmentId: number;
	department: string;
	departmentDescription: string;
	itemId: number;
	item: string;
	glAccountId: number;
	glAccount: string;
	glAccountDescription: string;
	budgetTypeId: number;
	budgetType: string;
	preAllocationAmount: number;
	finalAllocationAmount: number;
}

// Required to bind item dropdown list in right section detail - Dept Transfer
export interface ITransferItem {
	itemId: number;
	name: string;
	departmentId: number;
	programmeId: number;
}

// Required for monthly allocation dialog grid
export interface IMonthlyAllocationGridDetail {
	year: number;
	month: number;
	baselineAllocationAmount: number;
	displayOrder: number;
	changeOrderAmount: number;
	requisitions: number;
	sourcing: number;
	contracts: number;
	purchaseOrder: number;
	unpaidExpenses: number;
	paidExpenses: number;
	spendPercentage: number;
}

// Required to fetch and bind monthly allocation detail
export interface IMonthlyAllocationDetail {
	budgetAllocationLineDetailId: number;
	glAccount: string;
	description: string;
	glCategory: string;
	status: string;
	purchaseCategory: string;
	budgetAllocationNumber: string;
	financialYear: string;
	budgetType: string;
	budgetingStage: string;
	isMonthlyEvenSpread: boolean;
	baselineAllocationAmount: number;
	changeOrderAmount: number;
	budgetAllocationLineDetailDistributions: IMonthlyAllocationGridDetail[];
}

// For monthly allocation tile detail - Required for FE only
export interface IMonthlyAllocationTileDetail {
	budgetAllocated: number;
	budgetCommitted: number;
	budgetUsed: number;
	remainingBudget: number;
}

// Required for monthly allocation save/update API (request model)
export interface IMonthlyAllocationAddDetail {
	budgetAllocationLineDetailId: number;
	isMonthlyEvenSpread: boolean;
	lineDetailMonthlyDistributionInputs: {
		year: number;
		month: number;
		baselineAllocationAmount: number;
		changeOrderAmount: number;
		displayOrder: number;
	}[];
}

export interface FinanceAllocationStatusInput {
	budgetAllocationLineId: number | null;
	programmeId: number | null;
	departmentId: number | null;
	budgetTypeId: number | null;
	budgetAllocationLineAdjustmentId: number | null;
	budgetAllocationLineTransferId: number | null;
	changeRequestBudgetAllocationStepId: number | null;
	isApproved: boolean;
	isChangeRequested: boolean;
	isRejected: boolean;
	comment: string;
}

export interface DepartmentAllocationStatusInput {
	budgetAllocationLineDetailId: number | null;
	budgetAllocationLineId: number | null;
	itemId: number | null;
	glAccountId: number;
	budgetAllocationLineDetailAdjustmentId: number | null;
	budgetAllocationLineDetailTransferId: number | null;
	changeRequestBudgetAllocationStepDepartmentId: number | null;
	isApproved: boolean;
	isChangeRequested: boolean;
	isRejected: boolean;
	comment: string;
}

// Required to set detail on approve, reject, task complete and change request buttons from Budget allocation detail page
export class BudgetAllocationStepConfirmationDetail {
	comment: string = "";
	financeAllocationStatusInputs: FinanceAllocationStatusInput[] = [];
	departmentAllocationStatusInputs: DepartmentAllocationStatusInput[] = [];
}

// Required for FE, for budget allocation confirmation dialog
export class AllocationConfirmationDialogDetail {
	comment: string = "";
	changeRequestBudgetAllocationStepId: number | null = null;
	changeRequestBudgetAllocationStepDepartmentId: number | null = null;
}

// Required for FE, to update confirmation details for finance and department line detail
export class AllocationConfirmationTypeDetail {
	budgetAllocationStepId: number | null = null;
	budgetAllocationStepDepartmentId: number | null = null;
	budgetAllocationLineDetailId: number | null = null;
	budgetAllocationLineId: number | null = null;
	changeRequestBudgetAllocationStepId: number | null = null;
	changeRequestBudgetAllocationStepDepartmentId: number | null = null;
	isApproved: boolean = false;
	isRejected: boolean = false;
	isChangeRequested: boolean = false;
	isChangeRequestCompleted: boolean = false;
	comment: string = "";
	programmeId: number = -1;
	budgetTypeId: number = -1;
}

// Required for FE, for budget allocation confirmation dialog text
export class BudgetConfirmationDialogBaseDetail {
	dialogTitle: string = "";
	dialogConfirmationText: string = "";
	confirmationButtonText: string = "";
}

export interface IPreviousBudgetStepHistory {
	budgetAllocationStepDepartmentId: number | null;
	budgetAllocationStepId: number;
	stepNumber: number;
	sequenceNumber: number;
	description: string;
	assignedToUsers: string;
	isMainStep: boolean;
}

export class SubmitAllocationDetail {
	isSubmitAllocation: boolean = false;
	isSubmitAllocationStep: boolean = false;
	allocationCommentDialog: boolean = false;
	highlightCommentField: boolean = false;
	comment: string = "";
	type: string = "";
	message: string = "";
}

export interface IFinConsolidationStatusInput {
	budgetAllocationLineId: number;
	changeRequestBudgetAllocationStepId: number | null;
	isApproved: boolean;
	isRejected: boolean;
	isChangeRequested: boolean;
}
